import { Button, Card, Form } from '../../../ui';
import React, { Component, SyntheticEvent } from 'react';
import { Helmet } from 'react-helmet';
import { Redirect, RouteComponentProps, withRouter } from 'react-router-dom';
import { resetPassword } from '../../../../hooks/useAuth';
import { AuthFlowState, useAuthFlow } from '../../../../state/app-state';

const CodeQueryParam = 'c';

interface ConfirmPasswordResetPageProps extends RouteComponentProps {
  authFlow: AuthFlowState;
  resetPassword: (code: string, password: string) => void;
}

interface ConfirmPasswordResetPageState {
  code: string | null;
  password: string;
  passwordTouched?: boolean;
  wrongPasswordLength?: boolean;
  passwordConfirmation: string;
  passwordConfirmationTouched?: boolean;
  passwordConfirmationMismatch?: boolean;
}

// TODO(Jose): Convert this into a Functional component
class ConfirmPasswordResetPage extends Component<
  ConfirmPasswordResetPageProps,
  ConfirmPasswordResetPageState
> {
  constructor(props: ConfirmPasswordResetPageProps) {
    super(props);

    this.state = {
      password: '',
      passwordTouched: false,
      wrongPasswordLength: false,
      passwordConfirmation: '',
      passwordConfirmationTouched: false,
      passwordConfirmationMismatch: false,
      code: new URLSearchParams(this.props.location.search).get(CodeQueryParam),
    };

    if (!this.state.code) {
      props.history.push('/');
    }
  }

  async handleSubmit(event: SyntheticEvent) {
    event.preventDefault();
    this.props.resetPassword(this.state.code as string, this.state.password);
  }

  handlePasswordChange(event: React.ChangeEvent<HTMLInputElement>) {
    const value = event.target.value;

    const wrongPasswordLength = value.length < 6 || value.length > 32;
    this.setState({
      password: value,
      passwordTouched: true,
      wrongPasswordLength,
    });
  }

  handlePasswordConfirmationChange(event: React.ChangeEvent<HTMLInputElement>) {
    const value = event.target.value;

    const passwordConfirmationMismatch = value !== this.state.password;
    this.setState({
      passwordConfirmation: value,
      passwordConfirmationTouched: true,
      passwordConfirmationMismatch,
    });
  }

  render() {
    return (
      <div>
        <Helmet>
          <title>Confirm Password</title>
        </Helmet>
        <Card>
          <Card.Body>
            {this.redirect()}
            <h1 className="text-2xl font-bold text-gray-900 text-center mb-6">Reset password</h1>
            <div>{this.showForm()}</div>
          </Card.Body>
        </Card>
      </div>
    );
  }

  showForm() {
    return (
      <Form noValidate onSubmit={e => this.handleSubmit(e)}>
        <Form.Group controlId="password">
          <Form.Label>Password</Form.Label>
          <Form.Control
            type="password"
            isInvalid={this.state.passwordTouched && this.state.wrongPasswordLength}
            value={this.state.password}
            onChange={(e: any) => this.handlePasswordChange(e)}
            placeholder="At least 6 characters"
            required
          />
          {(() => {
            if (this.state.passwordTouched && this.state.wrongPasswordLength) {
              return (
                <Form.Text>Please create a password between 6 and 32 characters long</Form.Text>
              );
            }
          })()}
        </Form.Group>

        <Form.Group controlId="password-confirmation">
          <Form.Label>Password confirmation</Form.Label>
          <Form.Control
            type="password"
            isInvalid={
              this.state.passwordConfirmationTouched && this.state.passwordConfirmationMismatch
            }
            value={this.state.passwordConfirmation}
            onChange={(e: any) => this.handlePasswordConfirmationChange(e)}
            required
          />
          {(() => {
            if (this.state.passwordConfirmationTouched && this.state.passwordConfirmationMismatch) {
              return <Form.Text>Password confirmation doesn't match Password</Form.Text>;
            }
          })()}
        </Form.Group>

        <Button
          disabled={
            this.props.authFlow.isResettingPassword ||
            this.props.authFlow.isRequestingPasswordReset ||
            !this.state.passwordTouched ||
            this.state.wrongPasswordLength ||
            !this.state.passwordConfirmationTouched ||
            this.state.passwordConfirmationMismatch
          }
          className="mt-4 w-full flex"
          variant="purple"
          type="submit"
        >
          {this.props.authFlow.isResettingPassword ? 'Resetting...' : 'Reset password'}
        </Button>
      </Form>
    );
  }

  redirect() {
    if (this.props.authFlow.isPasswordResetConfirmed) {
      return <Redirect to="/users/signin"></Redirect>;
    }
  }
}

const ConfirmPasswordResetPageWithRouter = withRouter(ConfirmPasswordResetPage);

const ConfirmPasswordResetPageContainer = () => {
  const authFlow = useAuthFlow();
  return <ConfirmPasswordResetPageWithRouter authFlow={authFlow} resetPassword={resetPassword} />;
};

export default ConfirmPasswordResetPageContainer;
